import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function FocusSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="Focus Setup"
        subtitle="What you need to configure inside FOCUS POS so it can send orders correctly."
        logo="/static/wiki/focus-logo.png"
        backPath="/help-center/pos-configuration"
        backLabel="POS Configuration Guide"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>This document provides a step-by-step process for:</p>
          <ul className="wiki-list">
            <li>Connecting the <strong>Focus POS station</strong> to the OCB App.</li>
            <li>Validating live order transmission between <strong>Focus POS station</strong> and <strong>OCB App</strong>.</li>
            <li>Installing and configuring the client&apos;s <strong>OCB App</strong> and <strong>Focus POS station</strong>.</li>
          </ul>

          <h3>Requirements</h3>
          <ul className="wiki-list">
            <li><strong>Focus POS Station</strong> access with the necessary permissions;</li>
            <li>The <strong>IPv4 Address number</strong> provided by the Stream team;</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="configure-focus-pos" title="A - CONFIGURE FOCUS POS TO CONNECT WITH OCB APP">
        <Article>
          <h3>Access the Focus POS Settings</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In your <strong>Focus POS</strong> screen, your system may look slightly different, but you need to access the main settings.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-01.png" alt="Focus POS main screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-02.png" alt="Setup button" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click <strong>Setup</strong> which will open an Autologin prompt, and select <strong>Employee</strong> to log in.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-03.png" alt="Autologin prompt" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-04.png" alt="Setup menu" className="wiki-screenshot" />
          </div>

          <h3>Configure Station Settings</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the main <strong>Setup</strong> menu, click on <strong>Station Settings</strong>. This action displays a window on your screen.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-05.png" alt="Station Settings menu" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-06.png" alt="Station Settings dialog" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the <strong>Multiscreen</strong>, click on the <strong>Initialize</strong> tab or the <strong>External tab</strong> to locate the corresponding fields.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-07.png" alt="Initialize tab" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In <strong>Initialize</strong>, click on the <strong>IPv4 Address</strong> field to update it with the one provided by the Stream team. (This differs depending on location and setup).</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click <strong>Access</strong>, set it to <strong>5557</strong>, then hit <strong>Ok</strong> to update and confirm all the changes.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If you need extra help, request a real IPv4 Address from the Stream team. No lorem ipsum or bla bla bla.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Alternatively, if those fields are on the <strong>External</strong> tab, make sure to update them there instead.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-07.png" alt="Initialize/External tab" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-08.png" alt="Updated fields" className="wiki-screenshot" />
          </div>

          <h3>Restart Focus POS</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After completing the settings, go back to the main screen and access <strong>Station Functions</strong>. Focus also needs to be restarted in order to apply your new station settings.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You can restart the POS by accessing the <strong>Station Functions</strong> button.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-09.png" alt="Station Functions menu" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-10.png" alt="Station Functions button" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once you click on it, you should see the <strong>Station Shutdown</strong> option, then press this button to confirm the restart.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-01.png" alt="Focus POS screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-11.png" alt="Station Shutdown dialog" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-12.png" alt="Shutdown confirmation" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After the station shuts down, <strong>Restart</strong> it to start the applicant with new settings.</p>
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="validate-integration" title="B - VALIDATE THE INTEGRATION">
        <Article>
          <h3>Send an Order from Focus POS</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Access the POS main screen, enter your manager password and click the <strong>OK</strong> button. (This will take you into the ordering interface).</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-01.png" alt="Focus POS main screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once inside the <strong>Ordering Interface</strong>, select any available table to begin a new order.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-13.png" alt="Focus ordering interface" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Add an item (example: Coca-Cola).</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>As soon as the item is keyed in, the order should <strong>immediately</strong> appear inside <strong>OCB App</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/focus/focus-14.png" alt="OCB App showing order" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-15.png" alt="Focus order details" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-16.png" alt="Order confirmation in OCB App" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Now please <strong>Send Order</strong> - After sending, confirm that the <strong>Total Screen</strong> appears in the <strong>OCB App</strong> (If enabled).</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/focus/focus-14.png" alt="Focus POS send order" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/focus/focus-17.png" alt="Total screen in OCB App" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default FocusSetup
